import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import type { ScrollNavButtonProps } from "./scrollable-container.types";

/** Ancoragem vertical quando a seta flutua sobre o track. */
const FLOAT_Y_CLASS = {
    center: "inset-y-0 my-auto",
    top: "top-0",
    bottom: "bottom-0",
} as const;

/** Diâmetro do botão por size. */
const SIZE_CLASS = {
    xs: "h-6 w-6",
    sm: "h-8 w-8",
    md: "h-10 w-10",
} as const;

/**
 * Ícone por size — proporcional ao círculo (antes só o `sm` escalava, deixando
 * `xs`/`md` com o glifo no tamanho padrão do react-icons).
 */
const ICON_CLASS = {
    xs: "h-3 w-3",
    sm: "h-4 w-4",
    md: "h-5 w-5",
} as const;

/**
 * Recuo lateral do botão flutuante por size. Botões menores colam mais na borda
 * do track; o `md` mantém o recuo original (`left-1 md:left-2`).
 */
const FLOAT_X_CLASS = {
    xs: { left: "left-0.5", right: "right-0.5" },
    sm: { left: "left-1", right: "right-1" },
    md: { left: "left-1 md:left-2", right: "right-1 md:right-2" },
} as const;

/**
 * Botão circular das setas next/prev do ScrollableContainer. Serve os dois
 * modos: `floating` (absolute sobre o track, colado numa borda) ou fluxo normal
 * (agrupado no header). `disabled` esmaece em vez de sumir — usado no header,
 * onde o par fica sempre visível; no modo flutuante o container decide montar.
 */
export function ScrollNavButton({
    side,
    onClick,
    disabled = false,
    floatY = "center",
    floating = false,
    edgeless = false,
    rounded = "full",
    className,
    size = "md",
}: ScrollNavButtonProps) {
    const Icon = side === "left" ? FiChevronLeft : FiChevronRight;
    const floatX = edgeless ? (side === "left" ? "left-0" : "right-0") : FLOAT_X_CLASS[size][side];
    const roundedClass = rounded === "md" ? "rounded-md" : "rounded-full";

    return (
        <button
            type="button"
            data-component="scroll-nav-button"
            onClick={onClick}
            disabled={disabled}
            aria-label={side === "left" ? "Voltar" : "Avançar"}
            className={[
                "z-10 flex shrink-0 items-center justify-center bg-base-100 text-primary shadow-[0_1px_4px_rgb(0_0_0/0.15)] transition-colors hover:bg-primary hover:text-primary-content disabled:opacity-30 disabled:hover:bg-base-100 disabled:hover:text-primary",
                roundedClass,
                floating ? "absolute" : "",
                floating ? floatX : "",
                floating ? FLOAT_Y_CLASS[floatY] : "",
                SIZE_CLASS[size],
                className,
            ]
                .filter(Boolean)
                .join(" ")}
        >
            <Icon className={ICON_CLASS[size]} />
        </button>
    );
}

/**
 * Seta no estilo `edge-fade`: a seta fica sobre um gradiente que desvanece a
 * partir da cor do fundo até transparente, cobrindo a borda do track. A cor NÃO
 * é fixa — vem da CSS var `--nav-fade-color` (fallback transparente), definida
 * pelo consumidor. Sempre flutuante e colada na borda; some quando não há para
 * onde rolar (o container decide montar/desmontar).
 */
export function ScrollNavFade({
    side,
    onClick,
    size = "md",
}: Pick<ScrollNavButtonProps, "side" | "onClick" | "size">) {
    const Icon = side === "left" ? FiChevronLeft : FiChevronRight;
    const gradientDir = side === "left" ? "to right" : "to left";

    return (
        <button
            type="button"
            data-component="scroll-nav-fade"
            onClick={onClick}
            aria-label={side === "left" ? "Voltar" : "Avançar"}
            className={[
                "absolute inset-y-0 z-10 flex items-center text-primary-content transition-colors",
                side === "left" ? "left-0 pl-1 pr-4" : "right-0 pl-4 pr-1",
            ].join(" ")}
            style={{
                backgroundImage: `linear-gradient(${gradientDir}, var(--nav-fade-color, transparent), color-mix(in srgb, var(--nav-fade-color, transparent) 90%, transparent), transparent)`,
            }}
        >
            <Icon className={ICON_CLASS[size]} />
        </button>
    );
}
